import { localizeFacilityOrAddonName } from '../i18n/catalogLabels';
import type { AppLanguage } from '../i18n/types';
import { normalizeAssetListResponse } from './assetListNormalize';

export type AssetBookingAddonOption = {
  addonId: number;
  /** Human-readable label (description or name from catalog / asset). */
  label: string;
  price: number | null;
  /** Max bookable quantity from asset; `null` means no cap listed. */
  maxQuantity: number | null;
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function addonRowNumericId(row: Record<string, unknown>): number | null {
  const raw = row.addon_id ?? row.id;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parsePrice(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function parseAddonAvailableQuantity(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.floor(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? Math.floor(n) : null;
  }
  return null;
}

function firstNonEmptyString(row: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return null;
}

/** True when a string is only digits and matches this addon id (bad API label). */
function looksLikeAddonNumericToken(value: string, addonId: number): boolean {
  const t = value.trim();
  return t === String(addonId) || /^\d+$/.test(t);
}

function addonDisplayNameFromRow(row: Record<string, unknown>, addonId: number): string | null {
  const name = firstNonEmptyString(row, ['addon_name', 'name', 'title', 'label']);
  if (name && !looksLikeAddonNumericToken(name, addonId)) {
    return name;
  }
  const description = firstNonEmptyString(row, ['description', 'addon_description']);
  if (description && !looksLikeAddonNumericToken(description, addonId)) {
    return description;
  }
  return null;
}

/** Prefer catalog / asset names (e.g. “Childcare services”), not numeric description placeholders. */
export function addonOptionDisplayLabel(
  assetRow: Record<string, unknown>,
  catalogRow: Record<string, unknown> | undefined,
  addonId: number,
  language: AppLanguage = 'en',
): string {
  for (const src of [catalogRow, assetRow]) {
    if (!src) {
      continue;
    }
    const label = addonDisplayNameFromRow(src, addonId);
    if (label) {
      return localizeFacilityOrAddonName(label, language, 'addon');
    }
  }
  return language === 'he' ? `תוספת ${addonId}` : `Add-on ${addonId}`;
}

/** Map addon id → display name for errors (catalog + resolved booking choices). */
export function buildAddonIdToDisplayNameMap(
  choices: AssetBookingAddonOption[],
  catalogRows: Record<string, unknown>[] = [],
): Map<number, string> {
  const out = new Map<number, string>();
  for (const row of catalogRows) {
    const id = addonRowNumericId(row);
    if (id === null) {
      continue;
    }
    const label = addonDisplayNameFromRow(row, id);
    if (label) {
      out.set(id, label);
    }
  }
  for (const choice of choices) {
    if (choice.label.trim() && !isGenericAddonIdLabel(choice.label, choice.addonId)) {
      if (!looksLikeAddonNumericToken(choice.label, choice.addonId)) {
        out.set(choice.addonId, choice.label);
      }
    }
  }
  return out;
}

/** True when the label is only a generic id placeholder (not a catalog / asset name). */
export function isGenericAddonIdLabel(label: string, addonId: number): boolean {
  const t = label.trim();
  if (!t) {
    return true;
  }
  return new RegExp(`^add-?on\\s*#?${addonId}$`, 'i').test(t);
}

/** Prefer a human name in validation / API error text — never a bare numeric id when avoidable. */
export function addonLabelForErrorMessage(
  opt: AssetBookingAddonOption | undefined,
  addonId: number,
): string {
  if (opt?.label && !isGenericAddonIdLabel(opt.label, addonId)) {
    return opt.label;
  }
  return 'This add-on';
}

/**
 * Replace addon id references in API / validation messages with catalog or asset names.
 */
function messageLooksAddonRelated(message: string): boolean {
  return /\b(addon|add-on|add_on|quantity|exceed|available|maximum|max)\b/i.test(message);
}

export function humanizeBookingAddonErrorMessage(
  message: string,
  choices: AssetBookingAddonOption[],
  catalogRows: Record<string, unknown>[] = [],
): string {
  const nameById = buildAddonIdToDisplayNameMap(choices, catalogRows);
  if (!message.trim() || nameById.size === 0) {
    return message;
  }
  let out = message;
  const addonContext = messageLooksAddonRelated(message);

  for (const [addonId, label] of Array.from(nameById.entries())) {
    const quoted = `"${label.replace(/"/g, '\\"')}"`;
    const id = String(addonId);
    const patterns: RegExp[] = [
      new RegExp(`\\baddon\\s+with\\s+id\\s*['"]?${id}\\b`, 'gi'),
      new RegExp(`\\baddon[_\\s-]?id\\s*[:=]?\\s*['"]?${id}\\b`, 'gi'),
      new RegExp(`\\badd[_\\s-]?on\\s*[:=]?\\s*['"]?${id}\\b`, 'gi'),
      new RegExp(`\\badd-on\\s*#?${id}\\b`, 'gi'),
    ];
    for (const re of patterns) {
      out = out.replace(re, quoted);
    }
    if (addonContext) {
      out = out.replace(new RegExp(`\\b${id}\\b`, 'g'), quoted);
    }
  }
  out = out.replace(/\baddon\s+with\s+id\s+/gi, 'addon ');
  return out;
}

export function normalizeAddonsCatalog(raw: unknown): Record<string, unknown>[] {
  return normalizeAssetListResponse(raw);
}

/** Bookable add-ons for an asset (`addons` array) with labels from the add-ons catalog. */
export function resolveAssetBookingAddonOptions(
  asset: Record<string, unknown>,
  catalogRows: Record<string, unknown>[],
  language: AppLanguage = 'en',
): AssetBookingAddonOption[] {
  const addons = asset.addons;
  if (!Array.isArray(addons) || addons.length === 0) {
    return [];
  }
  const catalogById = new Map<number, Record<string, unknown>>();
  for (const row of catalogRows) {
    const id = addonRowNumericId(row);
    if (id !== null && !catalogById.has(id)) {
      catalogById.set(id, row);
    }
  }
  const out: AssetBookingAddonOption[] = [];
  const seen = new Set<number>();
  for (const item of addons) {
    if (!isPlainObject(item)) {
      continue;
    }
    const addonId = addonRowNumericId(item);
    if (addonId === null || seen.has(addonId)) {
      continue;
    }
    seen.add(addonId);
    const maxQuantity = parseAddonAvailableQuantity(item.available_quantity);
    if (maxQuantity !== null && maxQuantity < 1) {
      continue;
    }
    const catalog = catalogById.get(addonId);
    // Asset rows may reference ids that no longer exist in GET /assets/addons; booking create rejects them.
    if (catalogRows.length > 0 && !catalog) {
      continue;
    }
    out.push({
      addonId,
      label: addonOptionDisplayLabel(item, catalog, addonId, language),
      price: parsePrice(item.addon_price ?? item.price),
      maxQuantity,
    });
  }
  return out;
}

/** Quantities already on a booking row (`addons` array in list/GET body). */
export function extractBookingAddonQuantitiesFromBody(addons: unknown): Map<number, number> {
  const m = new Map<number, number>();
  if (!Array.isArray(addons)) {
    return m;
  }
  for (const item of addons) {
    if (!isPlainObject(item)) {
      continue;
    }
    const addonId = addonRowNumericId(item);
    if (addonId === null) {
      continue;
    }
    const q = parseAddonAvailableQuantity(item.quantity);
    m.set(addonId, Math.max(1, q ?? 1));
  }
  return m;
}

/**
 * Max quantity allowed on PATCH: pool left on the asset plus what this booking already holds.
 * The API's `available_quantity` on GET asset is remaining inventory, not including this booking.
 */
export function effectiveBookingAddonMaxQuantity(
  assetPoolRemaining: number | null,
  quantityOnThisBooking: number,
): number | null {
  if (assetPoolRemaining === null) {
    return null;
  }
  return Math.max(0, assetPoolRemaining) + Math.max(0, Math.floor(quantityOnThisBooking));
}

/** Adjust caps for edit flow and surface add-ons already on the booking when the pool shows 0 left. */
export function applyBookingEditAddonAvailability(
  assetChoices: AssetBookingAddonOption[],
  reservedOnBooking: ReadonlyMap<number, number>,
  bookingAddonRows: unknown,
  catalogRows: Record<string, unknown>[],
  language: AppLanguage = 'en',
): AssetBookingAddonOption[] {
  const catalogById = new Map<number, Record<string, unknown>>();
  for (const row of catalogRows) {
    const id = addonRowNumericId(row);
    if (id !== null && !catalogById.has(id)) {
      catalogById.set(id, row);
    }
  }

  const byId = new Map(assetChoices.map((c) => [c.addonId, c]));
  const out: AssetBookingAddonOption[] = assetChoices.map((c) => ({
    ...c,
    maxQuantity: effectiveBookingAddonMaxQuantity(
      c.maxQuantity,
      reservedOnBooking.get(c.addonId) ?? 0,
    ),
  }));

  if (!Array.isArray(bookingAddonRows)) {
    return out;
  }

  for (const item of bookingAddonRows) {
    if (!isPlainObject(item)) {
      continue;
    }
    const addonId = addonRowNumericId(item);
    if (addonId === null || byId.has(addonId)) {
      continue;
    }
    const reserved = reservedOnBooking.get(addonId) ?? 0;
    const maxQuantity = effectiveBookingAddonMaxQuantity(0, reserved);
    if (maxQuantity !== null && maxQuantity < 1) {
      continue;
    }
    const catalog = catalogById.get(addonId);
    out.push({
      addonId,
      label: addonOptionDisplayLabel(item, catalog, addonId, language),
      price: parsePrice(item.addon_price ?? item.price),
      maxQuantity,
    });
  }

  return out;
}

/** Selected add-on lines for create/PATCH (clamped to each choice `maxQuantity`). */
export function buildBookingAddonsWirePayload(
  choices: AssetBookingAddonOption[],
  bodyAddons: unknown,
): Array<{ addon_id: number; quantity: number }> {
  const quantities = extractBookingAddonQuantitiesFromBody(bodyAddons);
  const selectedIds = new Set(quantities.keys());
  return choices
    .filter((o) => selectedIds.has(o.addonId))
    .map((o) => ({
      addon_id: o.addonId,
      quantity: clampBookingAddonQuantity(quantities.get(o.addonId) ?? 1, o.maxQuantity),
    }));
}

type AddonAvailabilityTranslate = (
  key:
    | 'bookings.addon.available.one'
    | 'bookings.addon.available.upTo'
    | 'bookings.addon.onBooking.noneLeft.one'
    | 'bookings.addon.onBooking.noneLeft'
    | 'bookings.addon.onBooking.more.one'
    | 'bookings.addon.onBooking.more',
  params?: Record<string, string | number>,
) => string;

function addonAvailabilityEnglish(
  opt: AssetBookingAddonOption,
  quantityOnBooking: number,
  isEditingBooking: boolean,
): string | null {
  if (opt.maxQuantity === null) {
    return null;
  }
  if (!isEditingBooking) {
    return opt.maxQuantity === 1
      ? '1 available'
      : `Up to ${opt.maxQuantity} available`;
  }
  const reserved = Math.max(0, Math.floor(quantityOnBooking));
  const moreAvailable = Math.max(0, opt.maxQuantity - reserved);
  if (reserved > 0 && moreAvailable === 0) {
    return reserved === 1
      ? '1 on this booking (none left to add)'
      : `${reserved} on this booking (none left to add)`;
  }
  if (reserved > 0 && moreAvailable > 0) {
    return moreAvailable === 1
      ? `${reserved} on booking · 1 more available`
      : `${reserved} on booking · ${moreAvailable} more available`;
  }
  return opt.maxQuantity === 1 ? '1 available' : `Up to ${opt.maxQuantity} available`;
}

/** Label for add-on availability while editing a booking. */
export function formatBookingAddonAvailabilityLabel(
  opt: AssetBookingAddonOption,
  quantityOnBooking: number,
  isEditingBooking: boolean,
  t?: AddonAvailabilityTranslate,
): string | null {
  if (opt.maxQuantity === null) {
    return null;
  }
  if (!t) {
    return addonAvailabilityEnglish(opt, quantityOnBooking, isEditingBooking);
  }
  if (!isEditingBooking) {
    return opt.maxQuantity === 1
      ? t('bookings.addon.available.one')
      : t('bookings.addon.available.upTo', { max: opt.maxQuantity });
  }
  const reserved = Math.max(0, Math.floor(quantityOnBooking));
  const moreAvailable = Math.max(0, opt.maxQuantity - reserved);
  if (reserved > 0 && moreAvailable === 0) {
    return reserved === 1
      ? t('bookings.addon.onBooking.noneLeft.one')
      : t('bookings.addon.onBooking.noneLeft', { count: reserved });
  }
  if (reserved > 0 && moreAvailable > 0) {
    return moreAvailable === 1
      ? t('bookings.addon.onBooking.more.one', { reserved })
      : t('bookings.addon.onBooking.more', { reserved, more: moreAvailable });
  }
  return opt.maxQuantity === 1
    ? t('bookings.addon.available.one')
    : t('bookings.addon.available.upTo', { max: opt.maxQuantity });
}

/** Clamp booking quantity to what the asset lists as available. */
export function clampBookingAddonQuantity(
  requested: number,
  maxQuantity: number | null,
): number {
  const q = Math.max(1, Math.floor(requested));
  if (maxQuantity === null) {
    return q;
  }
  return Math.min(q, Math.max(1, maxQuantity));
}

/** Booking API only accepts addon ids that exist in GET `/assets/addons`. */
export function validateBookingAddonsInCatalog(
  addons: Array<{ addon_id: number; quantity: number }>,
  catalogRows: Record<string, unknown>[],
  choices: AssetBookingAddonOption[] = [],
): string | null {
  if (catalogRows.length === 0 || addons.length === 0) {
    return null;
  }
  const catalogIds = new Set<number>();
  for (const row of catalogRows) {
    const id = addonRowNumericId(row);
    if (id !== null) {
      catalogIds.add(id);
    }
  }
  const nameById = buildAddonIdToDisplayNameMap(choices, catalogRows);
  for (const row of addons) {
    if (!catalogIds.has(row.addon_id)) {
      const name = nameById.get(row.addon_id) ?? `Add-on ${row.addon_id}`;
      return `"${name}" (id ${row.addon_id}) is on this asset but not in the add-ons catalog — uncheck it or update the asset.`;
    }
  }
  return null;
}

export function validateBookingAddonQuantities(
  addons: Array<{ addon_id: number; quantity: number }>,
  choices: AssetBookingAddonOption[],
  catalogRows: Record<string, unknown>[] = [],
): string | null {
  const byId = new Map(choices.map((c) => [c.addonId, c]));
  const nameById = buildAddonIdToDisplayNameMap(choices, catalogRows);
  for (const row of addons) {
    const opt = byId.get(row.addon_id);
    const name =
      nameById.get(row.addon_id) ?? addonLabelForErrorMessage(opt, row.addon_id);
    if (!opt) {
      return `${name} is not offered for this asset.`;
    }
    if (opt.maxQuantity !== null && row.quantity > opt.maxQuantity) {
      return `Requested quantity for "${name}" exceeds available quantity (${opt.maxQuantity}).`;
    }
  }
  return null;
}
